import {
    IProFormaGeneralInfo,
    IOperatingInfo,
    IPermanentDebtInfo,
    IUsesSummary,
    ISourcesSummary,
    IOperatingPeriodCashFlows,
    IDispositionData,
    IProFormaReturnsData,
    IIncomeSummary,
} from 'views/ProFormaTable/types';
import { IProFormaExcelData } from './types';
import { exportProFormaToExcel } from './proFormaExcelExport';
import { formatBuildingInfoSheet } from './buildingInfoSheet';
import { formatSourcesUsesSheet } from './sourcesUsesSheet';
import { formatCashFlowSheet } from './cashFlowSheet';
import { formatDispositionSheet } from './dispositionSheet';
import { formatReturnsSummarySheet } from './returnsSummarySheet';
import { EXCEL_SHEET_NAMES } from './constants';
import { getUnitTypeOptions } from 'views/ProFormaTable/components/ProForma/ProFormaDisposition/helpers';
import {
    NON_APARTMENT_UNIT_TYPE_NAME,
    APARTMENT_UNIT_TYPE_NAME,
    STUDENT_HOUSING_UNIT_TYPE_NAME,
} from 'views/ProFormaTable/components/ProForma/ProFormaDisposition/constants';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';

export interface IExportProFormaToExcelParams {
    proForma: IProFormaGeneralInfo;
    operatingInfo: IOperatingInfo & IPermanentDebtInfo;
    incomeSummary: IIncomeSummary;
    usesSummary: IUsesSummary;
    sourcesSummary: ISourcesSummary;
    cashFlows: (IOperatingPeriodCashFlows | null)[];
    disposition: IDispositionData | undefined;
    returns: IProFormaReturnsData;
    showCents?: boolean;
    includeGainLossToLease?: boolean;
}

export const handleExportProFormaToExcel = async ({
    proForma,
    operatingInfo,
    incomeSummary,
    usesSummary,
    sourcesSummary,
    cashFlows,
    disposition,
    returns,
    showCents = false,
    includeGainLossToLease = true,
}: IExportProFormaToExcelParams): Promise<void> => {
    const dealName = proForma.dealName || proForma.name || 'Pro Forma';
    const today = new Date();
    const dateStr = `${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}${today.getFullYear()}`;
    const fileName = `${dealName} - Pro Forma - ${dateStr}`;

    // For apartment-with-retail, surface separate retail rentable SF and a retail
    // unit count on the Building Info sheet. Unit count source depends on how the
    // retail income is being calculated (tenants vs floor estimates).
    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const retailRentableSf = isApartmentWithRetail
        ? Number(incomeSummary?.totalRetailIncome?.totalSquareFootage) || undefined
        : undefined;
    const retailUnitCount = isApartmentWithRetail
        ? (operatingInfo?.incomeCalculationType === IncomeCalculationType.TENANTS
            ? incomeSummary?.incomeTenants?.length
            : incomeSummary?.incomeFloorEstimates?.length)
        : undefined;

    const excelData: IProFormaExcelData = {
        fileName,
        sheets: [
            {
                sheetName: EXCEL_SHEET_NAMES.BUILDING_INFO,
                sheetData: formatBuildingInfoSheet({
                    proForma,
                    operatingInfo,
                    totalRentableSf: incomeSummary?.totalRentableSf,
                    retailRentableSf,
                    retailUnitCount,
                    totalBeds: incomeSummary?.totalBeds,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.SOURCES_USES,
                sheetData: formatSourcesUsesSheet({
                    dealName,
                    usesSummary,
                    sourcesSummary,
                    totalUnits: proForma.estimatedUnits,
                    totalBeds: incomeSummary?.totalBeds,
                    grossSF: proForma.builtGrossSquareFootage,
                    totalRentableSf: incomeSummary?.totalRentableSf,
                    proFormaModelId: proForma.proFormaModelId,
                    showCents,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.CASH_FLOW,
                sheetData: formatCashFlowSheet({
                    dealName,
                    cashFlows,
                    operationBeginMonth: operatingInfo?.operationBeginMonth,
                    returns,
                    disposition,
                    usesSummary,
                    proFormaModelId: proForma.proFormaModelId,
                    showCents,
                    includeGainLossToLease,
                    incomeCalculationType: operatingInfo?.incomeCalculationType,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.DISPOSITION,
                sheetData: (() => {
                    const unitTypeOptions = getUnitTypeOptions(operatingInfo, proForma);
                    const showPerGrossSf = unitTypeOptions.some(opt => opt.value === NON_APARTMENT_UNIT_TYPE_NAME.GSF);
                    const showPerRentableSf = unitTypeOptions.some(
                        opt => opt.value === NON_APARTMENT_UNIT_TYPE_NAME.RSF || opt.value === APARTMENT_UNIT_TYPE_NAME.SF,
                    );
                    const showPerUnit = unitTypeOptions.some(opt => opt.value === APARTMENT_UNIT_TYPE_NAME.UNIT);
                    const showPerBed = unitTypeOptions.some(opt => opt.value === STUDENT_HOUSING_UNIT_TYPE_NAME.BED);

                    return formatDispositionSheet({
                        dealName,
                        disposition,
                        saleDate: proForma.saleDate,
                        saleMonth: proForma.saleMonth,
                        proFormaModelId: proForma.proFormaModelId,
                        showCents,
                        showPerGrossSf,
                        showPerRentableSf,
                        showPerUnit,
                        showPerBed,
                    });
                })(),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.RETURNS_SUMMARY,
                sheetData: formatReturnsSummarySheet({
                    dealName,
                    returns,
                    disposition,
                    operatingInfo,
                    proFormaModelId: proForma.proFormaModelId,
                    cashFlows,
                    stabilizedMonth: operatingInfo?.stabilizedMonth,
                    showCents,
                }),
            },
        ],
    };

    await exportProFormaToExcel(excelData);
};
